<template>
  <v-dialog :value="value" @input="$emit('close')" max-width="600px" scrollable content-class="plan-importer-dialog">
    <v-card class="importer-card">
      <div class="aurora-bg"></div>
      <div class="grid-bg"></div>

      <v-card-title class="importer-title">
        <v-icon left large color="primary">mdi-content-copy</v-icon>
        Split-Bibliothek

        <v-spacer></v-spacer>
        <v-btn icon color="white" @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-card-subtitle>Füge Splits aus alten Plänen zu deinem aktuellen hinzu.</v-card-subtitle>

      <v-card-text style="max-height: 70vh">
        <div v-if="isLoading" class="text-center pa-10">
          <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          <p class="mt-4 grey--text text--lighten-1">Plan wird aktualisiert...</p>
        </div>

        <div v-else-if="validOldPlans && validOldPlans.length > 0" class="plans-container">
          <div v-for="plan in validOldPlans" :key="plan._id" class="old-plan-card">
            <div class="plan-header">
              <div class="plan-title">{{ formatDate(plan.createdAt) }}</div>
            </div>
            <v-list color="transparent" dense>
              <v-list-item v-for="(split, index) in plan.splits.workouts" :key="index" class="split-item">
                <v-list-item-icon class="mr-3">
                  <v-icon color="grey lighten-1">mdi-weight-lifter</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="split-title white--text font-weight-bold">{{ split.title }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-tooltip top color="primary">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="add-btn" icon small @click="addSplitToCurrentPlan(split)" v-bind="attrs" v-on="on">
                        <v-icon>mdi-plus</v-icon>
                      </v-btn>
                    </template>
                    <span>Zum aktuellen Plan hinzufügen</span>
                  </v-tooltip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </div>
        </div>

        <div v-else class="text-center pa-10 grey--text empty-state">
          <v-icon size="80" class="mb-4" color="grey darken-1">mdi-database-off-outline</v-icon>
          <h3 class="text-h6 font-weight-bold mb-2">Keine alten Pläne</h3>
          <p>Es wurden keine früheren Pläne mit Splits gefunden.</p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
// Der Script-Teil bleibt exakt gleich wie in deiner letzten funktionierenden Version.
// Er wird hier der Vollständigkeit halber eingefügt.
import api from "@/api"
import moment from "moment"

export default {
  name: "OldPlansManagerDialog",
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    currentPlan: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      oldWorkoutPlans: [],
    }
  },
  computed: {
    validOldPlans() {
      if (!this.oldWorkoutPlans) return []
      return this.oldWorkoutPlans.filter((plan) => plan && plan.splits && Array.isArray(plan.splits.workouts) && plan.splits.workouts.length > 0)
    },
  },
  methods: {
    formatDate(date) {
      return "Plan vom " + moment(date).format("DD.MM.YYYY")
    },
    async addSplitToCurrentPlan(splitToAdd) {
      if (!this.currentPlan || !this.currentPlan._id) {
        this.$toast.error("Kein aktiver Plan vorhanden, um den Split hinzuzufügen.")
        return
      }
      this.isLoading = true
      try {
        const updatedPlan = JSON.parse(JSON.stringify(this.currentPlan))
        if (!updatedPlan.splits) {
          updatedPlan.splits = {}
        }
        if (!Array.isArray(updatedPlan.splits.workouts)) {
          updatedPlan.splits.workouts = []
        }
        const newSplit = JSON.parse(JSON.stringify(splitToAdd))
        updatedPlan.splits.workouts.push(newSplit)
        await api.workoutApi.updateTrainingsPlan(updatedPlan._id, updatedPlan)
        this.$emit("plan-updated")
      } catch (error) {
        console.error("Fehler beim Hinzufügen des Splits:", error)
        this.$toast.error("Der Split konnte nicht hinzugefügt werden.")
      } finally {
        this.isLoading = false
      }
    },
  },
  watch: {
    async value(isOpen) {
      if (isOpen) {
        // Annahme: diese API-Funktion existiert und holt die Pläne
        this.oldWorkoutPlans = await api.workoutApi.fetchCustomerWorkoutHistories()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Scoped Styles für das neue Design
::v-deep .plan-importer-dialog {
  background: transparent !important;
  box-shadow: none !important;
  overflow: visible;
}

.importer-card {
  background-color: #16181d !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px !important;
  box-shadow: 0px 20px 50px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;
  color: white;
}

// --- Hintergrund-Effekte ---
.aurora-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at top, rgba(0, 150, 63, 0.15), transparent 60%);
  pointer-events: none;
  animation: aurora-sway 8s infinite alternate ease-in-out;
}

.grid-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.5;
  pointer-events: none;
}

@keyframes aurora-sway {
  from {
    transform: translateX(-10%) scale(1.2);
  }
  to {
    transform: translateX(10%) scale(1.2);
  }
}

// --- Header ---
.importer-title {
  font-size: 1.5rem;
  font-weight: 700 !important;
  text-shadow: 0 0 10px rgba(0, 150, 63, 0.5);
}
.importer-card .v-card__subtitle {
  color: #a0a0a0;
}

// --- Plan-Karten ---
.plans-container {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.old-plan-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(5px);
  padding: 16px;
  transition: all 0.3s ease;
}

.plan-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.plan-title {
  font-weight: bold;
  font-size: 1.1rem;
  color: white;
}

.plan-date {
  font-size: 0.8rem;
  color: #a0a0a0;
}

// --- Split-Listen-Items ---
.split-item {
  border-radius: 12px;
  margin: 4px 0;
  transition: background-color 0.2s ease-in-out;
  padding: 4px 8px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }
}

.split-title {
  font-weight: 500;
}

.add-btn {
  background: rgba(0, 150, 63, 0.2);
  border: 1px solid var(--v-primary-base);
  color: white !important;
  transition: all 0.2s ease;

  &:hover {
    background: var(--v-primary-base);
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(0, 150, 63, 0.7);
  }
}

// --- Leerer Zustand ---
.empty-state {
  opacity: 0.7;
}
</style>
